import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Avatar,
} from "@mui/material";
import {
  Save,
  ArrowBack,
  CloudUpload,
  MiscellaneousServices,
  Delete,
} from "@mui/icons-material";
import {
  createServiceSubCategory,
  updateServiceSubCategory,
  getServiceSubCategoryById,
  getServiceCategories,
} from "../../../services/Api";

const CreateAndModifySubServices = () => {
  const { categoryId, subServiceId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(subServiceId);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    categoryId: categoryId || "",
    isActive: true,
    subCategoryImages: null,
  });

  const [imagePreview, setImagePreview] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories();
    if (isEdit && subServiceId) {
      fetchSubService();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, subServiceId]);

  const fetchCategories = async () => {
    try {
      const response = await getServiceCategories(1, 100);
      if (response.success || response.data) {
        const dataSrc = response.data || response;
        const list = Array.isArray(dataSrc)
          ? dataSrc
          : dataSrc.docs || dataSrc.data || dataSrc.serviceCategories || [];
        setCategories(list);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchSubService = async () => {
    try {
      setInitialLoading(true);
      const response = await getServiceSubCategoryById(subServiceId);
      if (response.success) {
        const service = response.data;
        setFormData({
          name: service.name || "",
          categoryId: service.categoryId || categoryId || "",
          isActive: service.isActive !== undefined ? service.isActive : true,
          subCategoryImages: null,
        });
        setImagePreview(service.image || service.subCategoryImages || "");
      }
    } catch (err) {
      console.error("Error fetching sub service:", err);
      setError(err.response?.data?.message || err.message || "Failed to fetch sub service details");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "name") {
      // if (hasNumber) {
      //   setError("Sub Service Name cannot contain numbers");
      //   return;
      // }

      setError(null);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        subCategoryImages: file,
      }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }
    if (!formData.categoryId) {
      setError("Category is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("categoryId", formData.categoryId);

      if (isEdit) {
        submitData.append("isActive", formData.isActive);
      }

      if (formData.subCategoryImages) {
        submitData.append("subCategoryImages", formData.subCategoryImages);
      }

      let response;
      if (isEdit) {
        response = await updateServiceSubCategory(subServiceId, submitData);
      } else {
        response = await createServiceSubCategory(submitData);
      }

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/services/${categoryId}/sub-services`);
        }, 1500);
      } else {
        setError(
          response.message ||
          `Failed to ${isEdit ? "update" : "create"} sub service`,
        );
      }
    } catch (err) {
      console.error("Error saving sub service:", err);
      setError(
        err.response?.data?.message ||
        `An error occurred while ${isEdit ? "updating" : "creating"} the sub service`,
      );
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={() => navigate(`/services/${categoryId}/sub-services`)}
            sx={{ color: "primary.main" }}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 300, color: "text.primary" }}
            >
              {isEdit ? "Edit Sub Service" : "Create Sub Service"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEdit
                ? "Update sub service information"
                : "Add a new sub service to the system"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 4 }}>
          Sub service {isEdit ? "updated" : "created"} successfully!
          Redirecting...
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "text.border",
          borderRadius: 2,
          p: 4,
        }}
      >
        <form onSubmit={handleSubmit} noValidate>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
            }}
          >
            {/* Left Column */}
            <Box>
              {/* Name Field */}
              <TextField
                fullWidth
                size="small"
                label="Sub Service Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ mb: 3 }}
              />

              {/* Category Field */}
              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel shrink>Category</InputLabel>
                <Select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleInputChange}
                  label="Category"
                  required
                >
                  <MenuItem value="" disabled>
                    Select Category
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id || cat._id} value={cat.id || cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Status Toggle (only for edit) */}
              {isEdit && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      name="isActive"
                      color="primary"
                    />
                  }
                  label="Active Status"
                  sx={{ mb: 3 }}
                />
              )}
            </Box>

            {/* Right Column */}
            <Box>
              {/* Image Upload */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                  Sub Service Image
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      position: "relative",
                      width: 100,
                      height: 100,
                      "&:hover .delete-overlay": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      src={imagePreview}
                      sx={{
                        width: 100,
                        height: 100,
                        border: "1px solid",
                        borderColor: "text.border",
                      }}
                    >
                      <MiscellaneousServices sx={{ fontSize: 40 }} />
                    </Avatar>
                    {imagePreview && (
                      <Box
                        className="delete-overlay"
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: 0,
                          transition: "opacity 0.2s",
                          borderRadius: 1,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setImagePreview("");
                          setFormData((prev) => ({
                            ...prev,
                            subCategoryImages: null,
                          }));
                        }}
                      >
                        <Delete sx={{ color: "white" }} />
                      </Box>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    sx={{ textTransform: "none" }}
                  >
                    Upload Image
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Button>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Recommended size: 300x300px. Max size: 5MB.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(`/services/${categoryId}/sub-services`)}
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              disabled={loading}
              sx={{
                textTransform: "none",
                backgroundColor: "primary.main",
                minWidth: 120,
              }}
            >
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateAndModifySubServices;
